import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentNavigationLevel,
  selectNavigatedCompany,
  selectNavigationScope,
} from "../store/reducers/workspaceReducer";
import {
  getScreens,
  getScreensByCompanyId,
  selectSignageScreensForScope,
} from "../store/reducers/screenReducer";
import { NavigationLevel, Resource, Scope } from "@nendaTypes/NendaTypes";
import PermissionsGate from "../components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { Box, Link, Typography, useTheme } from "@mui/material";
import { t } from "i18next";
import DefaultDataGrid, {
  RowOption,
} from "../ui-components/dataGrid/DefaultDataGrid";
import { GridColDef, GridValidRowModel } from "@mui/x-data-grid";
import { store } from "../store";
import {
  deleteCompanyGroup,
  deletePremiseGroup,
  getCompanyGroups,
  getPremiseGroups,
  selectGroupsByNavigationLevelWithInheritance,
} from "../store/reducers/groupReducer";
import CreateGroup from "../components/Group/Create/CreateGroup";
import { useNavigate } from "react-router-dom";
import PageTitle from "../ui-components/typography/PageTitle";
import ConfirmationDialog from "../ConfirmationDialog";

const visibleColumns = {
  name: true,
  createdAt: true,
  numberOfScreens: true,
};

const Groups = () => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [groupIdToDelete, setGroupIdToDelete] = useState<string | undefined>(
    undefined
  );
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();

  const scope = useSelector(selectNavigationScope);
  const scopeLevel = useSelector(selectCurrentNavigationLevel);
  const company = useSelector(selectNavigatedCompany);

  const groups = useSelector(selectGroupsByNavigationLevelWithInheritance);

  const screens = useSelector(selectSignageScreensForScope);

  useEffect(() => {
    if (scope.company) dispatch(getCompanyGroups(scope.company));
    if (scopeLevel === NavigationLevel.PREMISE) {
      if (scope.premise) dispatch(getPremiseGroups(scope.premise));
    } else if (scopeLevel === NavigationLevel.COMPANY) {
      if (scope.company) dispatch(getScreensByCompanyId(scope.company));
    }
  }, [scope.company, scope.premise, scopeLevel]);

  const screensByGroup = useMemo(() => {
    const map: Record<string, string[]> = {};

    screens.forEach(({ _id, groups = [] }) => {
      groups.forEach((gid) => {
        (map[gid] ??= []).push(_id);
      });
    });

    return map;
  }, [screens]);

  useEffect(() => {
    switch (scopeLevel) {
      case NavigationLevel.COMPANY:
        if (scope.company) store.dispatch(getScreensByCompanyId(scope.company));
        break;
      case NavigationLevel.PREMISE:
        if (scope.premise) store.dispatch(getScreens(scope.premise));
        break;
      default:
        break;
    }
  }, [scope.premise, scope.company, scopeLevel]);

  if (scopeLevel === NavigationLevel.ADMIN) {
    return null;
  }

  const parentId =
    scopeLevel === NavigationLevel.COMPANY ? scope.company : scope.premise;

  if (parentId === undefined) {
    return null;
  }

  const rows = useMemo(() => {
    return groups
      .map((group) => {
        const numberOfScreens = screensByGroup[group._id]?.length || 0;
        const type =
          group.premise === undefined
            ? NavigationLevel.COMPANY
            : NavigationLevel.PREMISE;

        if (group.premise && scopeLevel === NavigationLevel.COMPANY) return;

        return {
          id: group._id,
          type: type,
          name: group.name,
          createdAt: group.createdAt,
          numberOfScreens,
          isDeleteEnabled:
            scopeLevel === NavigationLevel.PREMISE
              ? group.premise === scope.premise
              : group.premise
                ? false
                : true,
        };
      })
      .filter((g) => g !== undefined);
  }, [groups, screensByGroup]);

  const canLinkToGroup =
    scopeLevel === NavigationLevel.PREMISE ? true : company?.canManagePremises;

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("customerportal.pages.groups.table.name") || "Group Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return canLinkToGroup ? (
          <Link
            sx={{ color: theme.palette.primary.dark, cursor: "pointer" }}
            onClick={() =>
              navigate(`/${scopeLevel}/${parentId}/group/${params.row.id}`)
            }
          >
            {params.value}
          </Link>
        ) : (
          <Typography variant="body1">{params.value}</Typography>
        );
      },
    },
    {
      field: "type",
      headerName: t("customerportal.pages.groups.table.type") || "Group Type",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body1">
          {params.value === "company"
            ? t("customerportal.pages.groups.table.company")
            : t("customerportal.pages.groups.table.premise")}
        </Typography>
      ),
    },
    {
      field: "createdAt",
      headerName:
        t("customerportal.pages.groups.table.created_at") || "Created At",
      flex: 1,

      renderCell: (params) => (
        <Typography variant="body1">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: "numberOfScreens",
      headerName:
        t("customerportal.pages.groups.table.number_of_screens") || "Screens",
      flex: 1,
      renderCell: (params) => {
        return <Typography variant="body1">{params.value}</Typography>;
      },
    },
  ];

  const handleDelete = (groupId: string) => {
    setIsDeleteDialogOpen(true);
    setGroupIdToDelete(groupId);
  };

  const handleDeleteDialogClose = (isConfirmed: boolean) => {
    const groupId = groupIdToDelete;
    setIsDeleteDialogOpen(false);
    setGroupIdToDelete(undefined);
    if (!groupId) return;
    if (!isConfirmed) return;
    if (
      scopeLevel !== NavigationLevel.PREMISE &&
      scopeLevel !== NavigationLevel.COMPANY
    ) {
      return;
    }

    if (scopeLevel === NavigationLevel.PREMISE) {
      scope.premise &&
        dispatch(
          deletePremiseGroup({
            companyOrPremiseId: scope.premise,
            id: groupId,
          })
        );
    }
    if (scopeLevel === NavigationLevel.COMPANY) {
      scope.company &&
        dispatch(
          deleteCompanyGroup({
            companyOrPremiseId: scope.company,
            id: groupId,
          })
        );
    }
  };

  const rowOptions = (row: GridValidRowModel): RowOption[] => [
    {
      label: t("common.delete"),
      value: "delete",
      disabled: !row.isDeleteEnabled,
      onClick: () => handleDelete(row.id),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", gap: "2rem", alignItems: "center" }}>
        <PageTitle title={t("customerportal.pages.groups.title")} />
        <CreateGroup createFor={scopeLevel} parentId={parentId} />
      </Box>
      <DefaultDataGrid
        rows={rows}
        columns={columns}
        visibleColumns={visibleColumns}
        rowOptions={rowOptions}
      />
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleDeleteDialogClose}
        title={t("customerportal.pages.groups.delete_group")}
        content={t(
          "customerportal.pages.groups.delete_confirmation.description"
        )}
      />
    </Box>
  );
};

const permissionGated = () => {
  return (
    <PermissionsGate
      restriction={{
        resource: Resource.Group,
        scopes: [Scope.CanEdit],
      }}
    >
      <Groups />
    </PermissionsGate>
  );
};

export default permissionGated;
